import KeyCloakService from '@/security/KeycloakService'
import {DateToAmericaSaoPaulo} from '@/utils/dateHelper.js'
import { ehErroDeChunk, recargaEmAndamento } from '@/utils/recargaPorChunk.js'

const APP_NAME = 'Trivenix Web'
const STACK_TRACE_LINES = 8
const THROTTLE_MS = 60_000

let webhookUrl = null
let routerInstance = null
const sent = new Map()

function trimStackTrace(stack, lines) {
  if (!stack) return null
  return stack.split('\n').slice(0, lines).join('\n')
}

function errorKey(error) {
  return `${error.message}|${error.stack?.split('\n')[1] ?? ''}`
}

function isThrottled(key) {
  const last = sent.get(key)
  if (last && Date.now() - last < THROTTLE_MS) return true
  sent.set(key, Date.now())
  if (sent.size > 200) {
    const oldest = sent.keys().next().value
    sent.delete(oldest)
  }
  return false
}

function collectContext() {
  const route = routerInstance?.currentRoute?.value
  let user = KeyCloakService.GetUserMail() ?? KeyCloakService.GetUserName() ?? null

  return {
    path: route?.fullPath ?? window.location.pathname,
    routeName: route?.name ?? null,
    user,
    userAgent: navigator.userAgent,
    // A versão que esta aba executa, injetada no build. O `app_version` do localStorage que se
    // lia aqui ninguém grava mais desde que o aviso de versão nova passou a usar a do bundle:
    // os cards saíam com a versão da última visita de antes dessa mudança.
    appVersion: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : null,
    timestamp: new Date().toISOString(),
  }
}

function buildCard(error, context, { source = 'unknown' } = {}) {
  const title = `Erro Frontend — ${APP_NAME}`

  const facts = [
    { title: 'Aplicação', value: APP_NAME },
    { title: 'Origem', value: source },
    { title: 'Rota', value: context.path },
    { title: 'Quando', value: DateToAmericaSaoPaulo(context.timestamp)  },
  ]

  if (context.routeName)
    facts.push({ title: 'Nome da rota', value: String(context.routeName) })
  if (context.user) facts.push({ title: 'Usuário', value: context.user })
  if (context.appVersion)
    facts.push({ title: 'Versão', value: context.appVersion })
  if (error.name && error.name !== 'Error')
    facts.push({ title: 'Exception', value: error.name })

  const body = [
    {
      type: 'TextBlock',
      text: title,
      weight: 'Bolder',
      size: 'Medium',
      wrap: true,
      color: 'Attention',
    },
    {
      type: 'TextBlock',
      text: error.message || String(error),
      wrap: true,
      spacing: 'Small',
    },
    { type: 'FactSet', facts },
  ]

  const stack = trimStackTrace(error.stack, STACK_TRACE_LINES)
  if (stack) {
    body.push({
      type: 'TextBlock',
      text: '**Stack trace (topo):**',
      wrap: true,
      spacing: 'Medium',
    })
    body.push({
      type: 'TextBlock',
      text: stack,
      wrap: true,
      fontType: 'Monospace',
      size: 'Small',
      isSubtle: true,
    })
  }

  return {
    type: 'message',
    attachments: [
      {
        contentType: 'application/vnd.microsoft.card.adaptive',
        contentUrl: null,
        content: {
          type: 'AdaptiveCard',
          $schema: 'https://adaptivecards.io/schemas/adaptive-card.json',
          version: '1.4',
          msteams: { width: 'Full' },
          body,
        },
      },
    ],
  }
}

async function send(card) {
  if (!webhookUrl) return
  try {
    await fetch(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(card),
    })
  } catch {
    // evitar loop
  }
}

function reportError(error, { source = 'unknown' } = {}) {
  if (!webhookUrl) return
  if (!(error instanceof Error)) error = new Error(String(error))
  // A página já está recarregando para buscar o chunk da versão nova: não é defeito a reportar.
  // Se a recarga não resolver, o mesmo erro volta com a recarga recusada, e aí é enviado.
  if (ehErroDeChunk(error) && recargaEmAndamento()) return

  const key = errorKey(error)
  if (isThrottled(key)) return

  const context = collectContext()
  const card = buildCard(error, context, { source })
  send(card).then(r => console.log(r))
}

function install(app, router) {
  webhookUrl = import.meta.env.VITE_TEAMS_ERROR_WEBHOOK_URL
  routerInstance = router

  if (!webhookUrl) return

  app.config.errorHandler = (err, instance, info) => {
    let source = `vue:${info}`
    const name = instance?.$options?.name ?? instance?.$options?.__name
    if (name) source += ` (${name})`
    reportError(err, { source })
    console.error(err)
  }

  window.addEventListener('error', (event) => {
    if (event.error) reportError(event.error, { source: 'window.onerror' })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason))
    reportError(error, { source: 'unhandledrejection' })
  })
}

export default { install }
